import moment from 'moment';
import _ from 'lodash';
import { ADOBE_TOPICS, SgSessionStorageService, SgSubmissionUtil } from 'saga-modules';
import SpmConstants from 'sg-spm-constants';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { publish } from '@jutro/events';

const getMomentDateFromDto = (dto) => {
    return moment(`${dto.year}${dto.monthViewValue}${dto.day}`, 'YYYYMMDD');
};

const calculateClaimAndConvictionDay = (dateDto, modelVM, csdPath) => {
    const result = { ...dateDto };
    const today = moment();
    if (dateDto.month !== undefined && dateDto.year !== undefined) {
        const csd = _.get(modelVM, `${csdPath}`);
        // by default set day as CSD day + 1;
        if (csd && csd.day) {
            result.day = `${Number.parseInt(csd.day, 10) + 1}`;
        }
        // set day as a day before csd day if incident is in the same month and year as current date
        if (csd.month === dateDto.month && dateDto.year === moment().format('yyyy')) {
            // unless it's first day of month (and claim is very same day) then set incident date as 1st
            result.day = (today.date() === 1 || Number.parseInt(csd.day, 10) === 1) ? '01' : `${Number.parseInt(csd.day, 10) - 1}`;
        }
        // if date is invalid (32nd day of month for example) - set day to last day of month
        if (!getMomentDateFromDto(result).isValid()) {
            result.day = 1; // set it to 1 so date is valid and we can get last day of month
            result.day = getMomentDateFromDto(result).endOf('month').format('DD');
        }
        // if date is in the future set current day.
        if (getMomentDateFromDto(result).isAfter(moment())) {
            result.day = new Date().getDate();
        }
        // make sure format is correct
        if (result.day.length === 1) {
            result.day = `0${result.day}`;
        }
        // in case there is month/year invalid and day cannot be calculated
        if (result.day === 'Invalid date') {
            result.day = '01';
        }
    }
    return result.day;
};

// replace underscore with spaces, split them, capitalize the text and join them
const formatString = (str) => {
    return !_.isNil(str) && str
        .replace(/_/g, ' ').split(' ')
        .map((x) => {
            return x.charAt(0).toUpperCase() + x.slice(1);
        })
        .join(' ');
};

const getCoverages = (modelVM) => {
    return _.get(modelVM.value || modelVM, 'lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0].coverages');
};

const getBasicMotorCover = (modelVM) => {
    return SgSubmissionUtil.getCoverageByCode(getCoverages(modelVM), SpmConstants.BASIC_MOTOR_COVER_CODE);
};

const getSpmVoluntaryExcess = (modelVM) => {
    const volTerm = SgSubmissionUtil.getCoverageByCode(getBasicMotorCover(modelVM).terms, SpmConstants.VOLUNTARY_AD_EXCESS_CODE);
    let result = 0;
    if (volTerm) {
        const volExcess = volTerm.chosenTermValue;
        if (!volExcess) {
            result = modelVM.lobData.privateMotor.coverables.spmVehicle.requestedVolExcess;
        } else {
            result = parseInt(volExcess.replace(/\u00A3/g, ''), 10);
        }
    }
    return result;
};

const getSpmCompulsoryExcess = (modelVM) => {
    const compulsoryExcess = SgSubmissionUtil.getCoverageByCode(getBasicMotorCover(modelVM).terms, SpmConstants.COMPULSORY_AD_EXCESS_CODE);
    if (compulsoryExcess) {
        return compulsoryExcess.directValue;
    }
    return 0;
};

const getSpmTotalExcess = (modelVM) => {
    const compExcess = getSpmCompulsoryExcess(modelVM);
    const volExcess = getSpmVoluntaryExcess(modelVM);
    return +compExcess + +volExcess;
};

const hasPNCD = (modelVM) => {
    return _.get(modelVM.value || modelVM, 'lobData.privateMotor.coverables.spmVehicle.isNCDProtected', false);
};

const getBasicMotorCoverTerm = (modelVM) => {
    const coverLevelTerm = SgSubmissionUtil.getCoverageByCode(getBasicMotorCover(modelVM).terms, SpmConstants.COVER_LEVEL_TERM_CODE);
    return coverLevelTerm.chosenTerm;
};

const isSagaStandard = (modelVM) => {
    return getBasicMotorCoverTerm(modelVM) === SpmConstants.COVER_LEVEL_CODES.SAGA_STANDARD;
};

const isSagaSelect = (modelVM) => {
    return getBasicMotorCoverTerm(modelVM) === SpmConstants.COVER_LEVEL_CODES.SAGA_SELECT;
};

const isSagaPlus = (modelVM) => {
    return getBasicMotorCoverTerm(modelVM) === SpmConstants.COVER_LEVEL_CODES.SAGA_PLUS;
};

const isThirdPartyFireAndTheft = (modelVM) => {
    return getBasicMotorCoverTerm(modelVM) === SpmConstants.COVER_LEVEL_CODES.THIRD_PARTY_FIRE_AND_THEFT;
};

const isThirdPartyOnly = (modelVM) => {
    return getBasicMotorCoverTerm(modelVM) === SpmConstants.COVER_LEVEL_CODES.THIRD_PARTY_ONLY;
};

const includes = (string, properties) => {
    if (!string) {
        return false;
    }

    const str = string.toLowerCase();
    return properties.some((prop) => {
        let p = prop;
        if (typeof p === 'string') {
            p = p.toLowerCase();
        }
        return str.includes(p);
    });
};

const getVehicleDisplayName = (vehicle) => {
    const properties = [vehicle.make, vehicle.model, vehicle.year, vehicle.bodyType, vehicle.transmission];
    const isDescriptionIncluded = includes(vehicle.description, properties);

    let displayName = `${vehicle.make.toUpperCase()} ${vehicle.model.toUpperCase()} ${vehicle.engineCapacity}cc (${vehicle.year})`;

    if (vehicle.numberOfDoors) {
        displayName += ` ${vehicle.numberOfDoors}-door`;
    }
    if (vehicle.bodyType) {
        displayName += ` ${vehicle.bodyType}`;
    }
    if (vehicle.transmission) {
        displayName += ` ${vehicle.transmission.charAt(0).toUpperCase()}${vehicle.transmission.slice(1).toLowerCase()}`;
    }
    if (vehicle.description && !isDescriptionIncluded) {
        displayName += ` ${vehicle.description.charAt(0).toUpperCase()}${vehicle.description.slice(1).toLowerCase()}`;
    }
    return displayName;
};

const isPNCDEligible = (modelVM) => {
    const pncdAddon = getCoverages(modelVM).find((el) => el.patternCode === SpmConstants.NCD_PROTECTION_CODE);
    return hasPNCD(modelVM) || (isSagaStandard(modelVM) && !_.isNil(pncdAddon));
};

const isAddingExistingPerson = (existingPerson, adderDriver) => {
    return existingPerson.firstName.toLowerCase() === adderDriver.person.firstName.toLowerCase().trim()
        && existingPerson.lastName.toLowerCase() === adderDriver.person.lastName.toLowerCase().trim()
        && parseInt(existingPerson.dateOfBirth.day, 10) === parseInt(adderDriver.person.dateOfBirth.day, 10)
        && parseInt(existingPerson.dateOfBirth.month, 10) === parseInt(adderDriver.person.dateOfBirth.month, 10)
        && parseInt(existingPerson.dateOfBirth.year, 10) === parseInt(adderDriver.person.dateOfBirth.year, 10);
};

const driverExists = (modelVM, driverIndex) => {
    const allDrivers = _.cloneDeep(modelVM.lobData.privateMotor.coverables.spmDrivers.value);
    if (allDrivers[driverIndex]) {
        const newDriver = _.cloneDeep(allDrivers[driverIndex]);
        const otherDrivers = _.filter(allDrivers, (driver) => allDrivers.indexOf(driver) !== driverIndex);
        if (newDriver.person.firstName && newDriver.person.lastName && newDriver.person.dateOfBirth) {
            for (let i = 0; i < allDrivers.length - 1; i += 1) {
                if (isAddingExistingPerson(otherDrivers[i].person, newDriver)) {
                    return true;
                }
            }
        }
        return false;
    }
    return false;
};

const getPNCDStepBackRules = (ncdValue) => {
    const ncdWording = ncdValue < 20 ? `${ncdValue} years` : '20 years';
    const zeroYears = '0 years';
    const oneYear = '1 year';
    const twoYears = '2 years';
    const threeYears = '3 years';
    return {
        fourOrLessYearsNCD: {
            withoutClaim: {
                noPNCD: {
                    oneClaimMade: twoYears,
                    twoClaimsMade: zeroYears,
                    threeClaimsMade: zeroYears
                },
                PNCD: {
                    oneClaimMade: ncdWording,
                    twoClaimsMade: ncdWording,
                    threeClaimsMade: twoYears
                }
            },
            withClaim: {
                noPNCD: {
                    oneClaimMade: twoYears,
                    twoClaimsMade: zeroYears,
                    threeClaimsMade: zeroYears
                },
                PNCD: {
                    oneClaimMade: ncdWording,
                    twoClaimsMade: twoYears,
                    threeClaimsMade: zeroYears
                }
            }
        },
        fiveOrMoreYearsNCD: {
            withoutClaim: {
                noPNCD: {
                    oneClaimMade: threeYears,
                    twoClaimsMade: oneYear,
                    threeClaimsMade: zeroYears
                },
                PNCD: {
                    oneClaimMade: ncdWording,
                    twoClaimsMade: ncdWording,
                    threeClaimsMade: threeYears
                }
            },
            withClaim: {
                noPNCD: {
                    oneClaimMade: threeYears,
                    twoClaimsMade: oneYear,
                    threeClaimsMade: zeroYears
                },
                PNCD: {
                    oneClaimMade: ncdWording,
                    twoClaimsMade: threeYears,
                    threeClaimsMade: oneYear
                }
            }
        }
    };
};

const hasFaultyClaim = (modelVM) => {
    const policyHolder = modelVM.lobData.privateMotor.coverables.spmDrivers.value.find((driver) => driver.isPolicyHolder);
    return _.some(policyHolder.cueClaims, (cue) => {
        return cue.cueFaultStatus === SpmConstants.CLAIM_FAULT_CUSTOMER;
    }) || _.some(policyHolder.claims, (claim) => {
        return claim.whoWasAtFault === SpmConstants.CLAIM_FAULT_CUSTOMER;
    });
};

const isVehicleLeased = (modelVM) => {
    const { vehicleOwnerType } = modelVM.lobData.privateMotor.coverables.value.spmVehicle;
    return (vehicleOwnerType === SpmConstants.OWNER_TYPE_LEASE_COMPANY || vehicleOwnerType === SpmConstants.OWNER_TYPE_LEASE_SCHEME_PURCHASE);
};

const updatePaymentPlan = async (modelVM, updateData, setQuoting) => {
    const customQuoteProps = {
        modelVM,
        updateData,
        setQuoting,
        coverages: {
            privateMotor: modelVM.lobData.privateMotor.offerings.value[0].coverages
        }
    };
    await SgSubmissionUtil.updatePaymentPlan(customQuoteProps);
};

const checkAndSetMFMonthlyPlanSelected = (modelVM, updateData) => {
    if (modelVM.value.isExpiredQuote) {
        return;
    }
    const { coverables } = modelVM.lobData.privateMotor;
    if (modelVM.bindData.paymentPlans.value.length > 1) {
        if (coverables.isMFMonthlyPlanSelected_Ext.value === undefined) {
            coverables.isMFMonthlyPlanSelected_Ext = coverables.spmVehicle.payByInstalments.value;
            LoadSaveService.updateQuotedSubmission(modelVM.value);
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.SELECTED_PAYMENT_ID, coverables.spmVehicle.payByInstalments.value
                ? SgSubmissionUtil.getMonthlyUpfrontPaymentPlan(modelVM).billingId
                : SgSubmissionUtil.getAnnualPaymentPlan(modelVM).billingId);
        } else {
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.SELECTED_PAYMENT_ID, coverables.isMFMonthlyPlanSelected_Ext.value
                ? SgSubmissionUtil.getMonthlyUpfrontPaymentPlan(modelVM).billingId
                : SgSubmissionUtil.getAnnualPaymentPlan(modelVM).billingId);
        }
    }
    const newModelVM = _.clone(modelVM);
    updateData(newModelVM);
};

const hasToUpdatePHDriverClaims = (modelVM) => {
    const phDriver = _.find(modelVM.lobData.privateMotor.coverables.spmDrivers.value, { isPolicyHolder: true });
    return phDriver.hasMotorClaims && _.isEmpty(phDriver.claims);
};

const hasToUpdateAdditionalDriverClaims = (modelVM) => {
    const additionalDrivers = _.filter(modelVM.lobData.privateMotor.coverables.spmDrivers.value, { isPolicyHolder: false });
    return !_.isEmpty(additionalDrivers) && _.some(additionalDrivers, (driver) => {
        return driver.hasMotorClaims && _.isEmpty(driver.claims);
    });
};

const hasToUpdateCarsInHousehold = (modelVM) => {
    const phDriver = _.find(modelVM.lobData.privateMotor.coverables.spmDrivers.value, { isPolicyHolder: true });
    return !!phDriver.hasAccessToOtherVehicles && _.isNil(phDriver.carsKeptAtHousehold);
};

const claimsRequireCustomerInput = (modelVM) => {
    const { spmDrivers } = modelVM.value.lobData.privateMotor.coverables;
    return spmDrivers.some((driver) => {
        return driver.claims.some((claim) => {
            return (claim.type === SpmConstants.CLAIM_TYPES.THEFT
                || claim.type === SpmConstants.CLAIM_TYPES.FIRE
                || claim.type === SpmConstants.CLAIM_TYPES.VANDALISM
                || claim.type === SpmConstants.CLAIM_TYPES.STORM);
        });
    });
};

const hasSpecifiedModifications = (modelVM) => {
    let specifiedModificationsExist = false;
    const { vehicleModifications } = modelVM.lobData.privateMotor.coverables.value.spmVehicle;
    if (vehicleModifications) {
        vehicleModifications.forEach((modification) => {
            if (SpmConstants.MODIFICATION_TYPES.includes(modification.modificationType)) {
                specifiedModificationsExist = true;
            }
        });
    }
    return specifiedModificationsExist;
};

const trackClaimsChange = (modelVM) => {
    const drivers = modelVM.lobData.privateMotor.coverables.spmDrivers.value;
    publish(ADOBE_TOPICS.CLAIMS_CHANGE, drivers);
};

const showGatekeeperQuestion = (modelVM) => {
    return !SgSubmissionUtil.isQuoted(modelVM) || SgSubmissionUtil.isResolQuote(modelVM) || SgSubmissionUtil.isIndicativeQuote(modelVM);
};

const isElectricVehicle = (modelVM) => {
    const vehicle = modelVM.lobData.privateMotor.coverables.value.spmVehicle;
    return vehicle.engineCapacity === SpmConstants.ELECTRIC_VEHICLE_ENGINE_SIZE;
};

const resetDocusign = (modelVM, isAprZero, updateData, adobeDocusign) => {
    const isMonthly = SgSubmissionUtil.isMonthlyPlanSelected(modelVM);
    const newModelVM = _.clone(modelVM);
    if (modelVM.isDocuSign_Ext.value === true) {
        if (!isMonthly || (isMonthly && isAprZero)) {
            publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: adobeDocusign, value: undefined });
            _.set(newModelVM, 'isDocuSign_Ext.value', undefined);
        }
    }
    updateData(newModelVM);
};

export default {
    calculateClaimAndConvictionDay,
    formatString,
    hasPNCD,
    isSagaStandard,
    driverExists,
    isSagaSelect,
    isSagaPlus,
    getSpmVoluntaryExcess,
    getSpmTotalExcess,
    getSpmCompulsoryExcess,
    getVehicleDisplayName,
    isPNCDEligible,
    getCoverages,
    getPNCDStepBackRules,
    hasFaultyClaim,
    isThirdPartyFireAndTheft,
    isThirdPartyOnly,
    updatePaymentPlan,
    checkAndSetMFMonthlyPlanSelected,
    hasToUpdatePHDriverClaims,
    hasToUpdateAdditionalDriverClaims,
    hasToUpdateCarsInHousehold,
    isVehicleLeased,
    claimsRequireCustomerInput,
    hasSpecifiedModifications,
    trackClaimsChange,
    showGatekeeperQuestion,
    isElectricVehicle,
    resetDocusign
};
